// Tunable thresholds for scoring. Defaults are starting points for the
// validation experiment (see docs/VALIDATION.md), not established standards.

export type Strictness = 'relaxed' | 'standard' | 'strict';

export interface AnalysisParams {
  /** Target-region half-size along the stringer, cm. */
  tolAlong: number;
  /** Target-region half-size across the board, cm. */
  tolAcross: number;
  /** Foot-angle tolerance, degrees (only used when checkAngle is on). */
  angleTolDeg: number;
  checkAngle: boolean;
  /** How long both feet must stay still to count as a controlled hold, ms. */
  holdMs: number;
  /** Shortest stillness that counts as a foot touching down, ms. */
  touchMs: number;
  /** A foot is "still" while its samples stay within this radius, cm. */
  stillRadius: number;
  /** A replant at least this far from the previous one is a step, cm. */
  stepMin: number;
  /** A rotation of at least this much without a step is a pivot, degrees. */
  pivotDeg: number;
  /** Minimum landmark visibility for a foot to be used. */
  minVisibility: number;
}

export const STRICTNESS: Record<Strictness, { tolAlong: number; tolAcross: number; angleTolDeg: number }> = {
  relaxed: { tolAlong: 12, tolAcross: 12, angleTolDeg: 35 },
  standard: { tolAlong: 8, tolAcross: 8, angleTolDeg: 25 },
  strict: { tolAlong: 5, tolAcross: 5, angleTolDeg: 18 },
};

export const DEFAULT_PARAMS: AnalysisParams = {
  ...STRICTNESS.standard,
  checkAngle: false,
  holdMs: 1000,
  touchMs: 100,
  stillRadius: 3,
  stepMin: 6,
  pivotDeg: 20,
  minVisibility: 0.5,
};

/**
 * Adapts noise-sensitive thresholds to the measured tracking jitter (σ, cm) of
 * this camera setup, so a noisy view doesn't turn standing still into "steps".
 */
export function paramsFor(base: Partial<AnalysisParams> & { strictness?: Strictness }, noise: number): AnalysisParams {
  const p: AnalysisParams = { ...DEFAULT_PARAMS, ...(base.strictness ? STRICTNESS[base.strictness] : {}), ...stripUndefined(base) };
  const stillRadius = Math.min(6, Math.max(p.stillRadius, 3 * noise));
  return { ...p, stillRadius, stepMin: Math.max(p.stepMin, 2 * stillRadius) };
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [k, v] of Object.entries(o)) if (v !== undefined && k !== 'strictness') (out as Record<string, unknown>)[k] = v;
  return out;
}

/** Bump when analysis logic changes, so stored attempts can be re-scored. */
export const ALGORITHM_VERSION = 1;
